import React from 'react';
import { Control, Controller, FieldError } from 'react-hook-form';
import {
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
  CircularProgress,
  Box,
  FormHelperText,
} from '@material-ui/core';
import { useTermsAndConditions } from '../hooks/useTermsAndConditions';
import { FormInputTypes, IFormInputs } from '../complaint-types';

interface ITermsAndConditionsProps {
  control: Control<IFormInputs>;
  name: FormInputTypes.tosVersionAccepted;
  error?: FieldError;
}

function TermsAndConditions(props: ITermsAndConditionsProps): JSX.Element {
  const { control, name, error: formFieldError } = props;
  const { data, loading, error: apiError } = useTermsAndConditions();

  const renderCheckbox = (
    isChecked: boolean,
    handleChange: (acceptedVersion: string | null) => void,
    isEnabled: boolean,
    labelContent: JSX.Element
  ) => (
    <FormControlLabel
      control={
        <Checkbox
          onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            if (isEnabled) {
              handleChange(checked && data?.version ? data.version : null);
            }
          }}
          checked={isChecked}
          disabled={!isEnabled}
        />
      }
      label={labelContent}
    />
  );

  return (
    <Box my={2}>
      <Controller
        name={name}
        control={control}
        rules={{ required: 'You must agree to the Terms and Conditions in order to continue.' }}
        render={({ onChange, onBlur, value, name: fieldName, ref }) => {
          const commonLabelStart = 'I agree to the ';

          if (loading) {
            return (
              <>
                {renderCheckbox(
                  !!value,
                  onChange,
                  false,
                  <Typography>
                    {commonLabelStart}
                    Terms and Conditions (Loading...){' '}
                    <CircularProgress
                      size={16}
                      style={{ marginLeft: '8px' }}
                      aria-label="Loading terms and conditions"
                    />
                  </Typography>
                )}
                <FormHelperText style={{ marginTop: '8px', fontSize: '12px' }}>
                  Please wait while we load the terms.
                </FormHelperText>
              </>
            );
          }

          if (apiError || !data) {
            return (
              <>
                {renderCheckbox(
                  !!value,
                  onChange,
                  false,
                  <Typography>
                    {commonLabelStart}
                    Terms and Conditions (unavailable)
                  </Typography>
                )}
                <FormHelperText error style={{ marginTop: '8px', fontSize: '12px' }}>
                  {apiError?.message || 'Failed to load Terms and Conditions. Please try again later.'}
                  <br />
                  You must be able to view and agree to the terms to submit the form.
                </FormHelperText>
              </>
            );
          }

          return (
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                    onChange(checked ? data.version : null);
                  }}
                  onBlur={onBlur}
                  checked={!!value}
                  name={fieldName}
                  inputRef={ref}
                />
              }
              label={
                <Typography>
                  {commonLabelStart}
                  <Link href={data.pdfLink} target="_blank" rel="noopener noreferrer">
                    Terms and Conditions
                  </Link>
                </Typography>
              }
            />
          );
        }}
      />
      {formFieldError && (
        <FormHelperText error style={{ marginTop: '8px', fontSize: '12px' }}>
          {formFieldError.message}
        </FormHelperText>
      )}
    </Box>
  );
}

export { TermsAndConditions };
